import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import api from '../services/api';
import DexScreenerModal from '../components/DexScreenerModal';
import { useAutoRefresh, AutoRefreshBadge } from '../hooks/useAutoRefresh';
import { fmtUsd, fmtPct, pnlClass, fmtAddr } from '../components/FrontrunEventModal';

function statusBadge(status) {
  const map = {
    simulated: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
    executed: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
    skipped: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
    failed: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
  };
  return map[status] || map.skipped;
}

function toForm(ev) {
  return {
    tokenSymbol: ev.tokenSymbol || '',
    tokenName: ev.tokenName || '',
    amountNative: ev.amountNative != null ? String(ev.amountNative) : '',
    marketCap: ev.marketCap != null ? String(ev.marketCap) : '',
    priceUsd: ev.priceUsd != null ? String(ev.priceUsd) : '',
    estimatedTokens: ev.estimatedTokens != null ? String(ev.estimatedTokens) : '',
    estimatedUsd: ev.estimatedUsd != null ? String(ev.estimatedUsd) : '',
    nativeUsd: ev.nativeUsd != null ? String(ev.nativeUsd) : '',
    status: ev.status || 'simulated',
    dryRun: ev.dryRun !== false,
    monitoredWalletLabel: ev.monitoredWalletLabel || '',
    createdAt: ev.createdAt
      ? new Date(ev.createdAt).toISOString().slice(0, 16)
      : '',
  };
}

export default function FrontrunEventDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [event, setEvent] = useState(null);
  const [target, setTarget] = useState(null);
  const [error, setError] = useState('');
  const [chartOpen, setChartOpen] = useState(false);
  const [editing, setEditing] = useState(searchParams.get('edit') === '1');
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');

  const fetchDetail = useCallback(async () => {
    try {
      const res = await api.get(`/frontrun/events/${id}`);
      setEvent(res.data.event);
      setTarget(res.data.target || null);
      setError('');
      if (!editing) setForm(toForm(res.data.event));
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load');
    }
  }, [id, editing]);

  const { loading, refreshing, lastUpdated, refresh } = useAutoRefresh(fetchDetail, [fetchDetail]);

  useEffect(() => {
    if (event && !form) setForm(toForm(event));
  }, [event, form]);

  useEffect(() => {
    if (searchParams.get('edit') === '1') setEditing(true);
  }, [searchParams]);

  const clearEditParam = () => {
    if (searchParams.get('edit') == null) return;
    const next = new URLSearchParams(searchParams);
    next.delete('edit');
    setSearchParams(next, { replace: true });
  };

  const startEdit = () => {
    setForm(toForm(event));
    setEditing(true);
    setSaveMsg('');
    setSearchParams({ edit: '1' }, { replace: true });
  };

  const cancelEdit = () => {
    setForm(toForm(event));
    setEditing(false);
    setSaveMsg('');
    clearEditParam();
  };

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSaveMsg('');
    try {
      const payload = {
        tokenSymbol: form.tokenSymbol || null,
        tokenName: form.tokenName || null,
        amountNative: Number(form.amountNative),
        marketCap: form.marketCap === '' ? null : Number(form.marketCap),
        priceUsd: form.priceUsd === '' ? null : Number(form.priceUsd),
        estimatedTokens: form.estimatedTokens === '' ? null : Number(form.estimatedTokens),
        estimatedUsd: form.estimatedUsd === '' ? null : Number(form.estimatedUsd),
        nativeUsd: form.nativeUsd === '' ? null : Number(form.nativeUsd),
        status: form.status,
        dryRun: form.dryRun,
        monitoredWalletLabel: form.monitoredWalletLabel || null,
      };
      if (form.createdAt) {
        payload.createdAt = new Date(form.createdAt).toISOString();
      }
      const res = await api.put(`/frontrun/events/${id}`, payload);
      setEvent(res.data.event);
      setTarget(res.data.target || null);
      setForm(toForm(res.data.event));
      setEditing(false);
      clearEditParam();
      setSaveMsg('Saved');
      setTimeout(() => setSaveMsg(''), 2000);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this frontrun entry?')) return;
    try {
      await api.delete(`/frontrun/events/${id}`);
      navigate('/frontrun');
    } catch (err) {
      setError(err.response?.data?.error || 'Delete failed');
    }
  };

  if (loading && !event) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-xl text-gray-600 dark:text-gray-400">Loading entry…</div>
      </div>
    );
  }

  if (error && !event) {
    return (
      <div className="space-y-4">
        <Link to="/frontrun" className="text-sm text-blue-600 hover:underline">← Frontrun</Link>
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">{error}</div>
      </div>
    );
  }

  const pnl = event?.pnl || {};
  const live = event?.live || {};

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Link to="/frontrun" className="text-sm text-blue-600 hover:underline">← Frontrun</Link>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
            {event.tokenSymbol || 'Token'}
            <span className="ml-2 text-sm font-normal uppercase text-gray-500">{event.chain}</span>
          </h1>
          <div className="flex flex-wrap items-center gap-2 mt-1">
            <span className={`px-2 py-0.5 rounded text-xs ${statusBadge(event.status)}`}>{event.status}</span>
            {event.dryRun !== false && (
              <span className="text-xs text-amber-600 dark:text-amber-400">Dry run</span>
            )}
            <span className="text-xs text-gray-500 font-mono">{event.tokenAddress}</span>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <AutoRefreshBadge lastUpdated={lastUpdated} refreshing={refreshing} />
          {!editing ? (
            <button
              type="button"
              onClick={startEdit}
              className="px-3 py-1.5 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700"
            >
              Edit entry
            </button>
          ) : (
            <button
              type="button"
              onClick={cancelEdit}
              className="px-3 py-1.5 text-sm rounded-lg bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-100"
            >
              Cancel
            </button>
          )}
          <button
            type="button"
            onClick={refresh}
            className="px-3 py-1.5 text-sm rounded-lg bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-100"
            disabled={editing}
          >
            Refresh
          </button>
          <button
            type="button"
            onClick={() => setChartOpen(true)}
            className="px-3 py-1.5 text-sm rounded-lg bg-green-600 text-white hover:bg-green-700"
          >
            Chart
          </button>
          {event.links?.explorerTx && (
            <a
              href={event.links.explorerTx}
              target="_blank"
              rel="noopener noreferrer"
              className="px-3 py-1.5 text-sm rounded-lg bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-100"
            >
              Source tx
            </a>
          )}
          <button
            type="button"
            onClick={handleDelete}
            className="px-3 py-1.5 text-sm rounded-lg text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
          >
            Delete
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded text-sm">{error}</div>
      )}
      {saveMsg && (
        <div className="bg-green-100 border border-green-400 text-green-800 px-4 py-2 rounded text-sm">{saveMsg}</div>
      )}

      {editing && form ? (
        <form
          onSubmit={handleSave}
          className="bg-white dark:bg-gray-800 rounded-xl shadow p-5 border border-blue-200 dark:border-blue-900/50 space-y-4"
        >
          <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Edit entry</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <label className="block text-sm">
              <span className="text-xs text-gray-500">Symbol</span>
              <input
                className="mt-1 w-full px-3 py-2 border rounded-lg dark:bg-gray-900 dark:border-gray-600"
                value={form.tokenSymbol}
                onChange={(e) => setField('tokenSymbol', e.target.value)}
              />
            </label>
            <label className="block text-sm">
              <span className="text-xs text-gray-500">Name</span>
              <input
                className="mt-1 w-full px-3 py-2 border rounded-lg dark:bg-gray-900 dark:border-gray-600"
                value={form.tokenName}
                onChange={(e) => setField('tokenName', e.target.value)}
              />
            </label>
            <label className="block text-sm">
              <span className="text-xs text-gray-500">Wallet label</span>
              <input
                className="mt-1 w-full px-3 py-2 border rounded-lg dark:bg-gray-900 dark:border-gray-600"
                value={form.monitoredWalletLabel}
                onChange={(e) => setField('monitoredWalletLabel', e.target.value)}
              />
            </label>
            <label className="block text-sm">
              <span className="text-xs text-gray-500">Entry market cap (USD)</span>
              <input
                type="number"
                step="any"
                className="mt-1 w-full px-3 py-2 border rounded-lg dark:bg-gray-900 dark:border-gray-600"
                value={form.marketCap}
                onChange={(e) => setField('marketCap', e.target.value)}
              />
            </label>
            <label className="block text-sm">
              <span className="text-xs text-gray-500">Entry price (USD)</span>
              <input
                type="number"
                step="any"
                className="mt-1 w-full px-3 py-2 border rounded-lg dark:bg-gray-900 dark:border-gray-600"
                value={form.priceUsd}
                onChange={(e) => setField('priceUsd', e.target.value)}
              />
            </label>
            <label className="block text-sm">
              <span className="text-xs text-gray-500">Native size</span>
              <input
                type="number"
                step="any"
                min="0.0001"
                required
                className="mt-1 w-full px-3 py-2 border rounded-lg dark:bg-gray-900 dark:border-gray-600"
                value={form.amountNative}
                onChange={(e) => setField('amountNative', e.target.value)}
              />
            </label>
            <label className="block text-sm">
              <span className="text-xs text-gray-500">Est. tokens</span>
              <input
                type="number"
                step="any"
                className="mt-1 w-full px-3 py-2 border rounded-lg dark:bg-gray-900 dark:border-gray-600"
                value={form.estimatedTokens}
                onChange={(e) => setField('estimatedTokens', e.target.value)}
              />
            </label>
            <label className="block text-sm">
              <span className="text-xs text-gray-500">Entry value (USD)</span>
              <input
                type="number"
                step="any"
                className="mt-1 w-full px-3 py-2 border rounded-lg dark:bg-gray-900 dark:border-gray-600"
                value={form.estimatedUsd}
                onChange={(e) => setField('estimatedUsd', e.target.value)}
              />
            </label>
            <label className="block text-sm">
              <span className="text-xs text-gray-500">Native USD price</span>
              <input
                type="number"
                step="any"
                className="mt-1 w-full px-3 py-2 border rounded-lg dark:bg-gray-900 dark:border-gray-600"
                value={form.nativeUsd}
                onChange={(e) => setField('nativeUsd', e.target.value)}
              />
            </label>
            <label className="block text-sm">
              <span className="text-xs text-gray-500">Status</span>
              <select
                className="mt-1 w-full px-3 py-2 border rounded-lg dark:bg-gray-900 dark:border-gray-600"
                value={form.status}
                onChange={(e) => setField('status', e.target.value)}
              >
                <option value="simulated">simulated</option>
                <option value="executed">executed</option>
                <option value="skipped">skipped</option>
                <option value="failed">failed</option>
              </select>
            </label>
            <label className="block text-sm">
              <span className="text-xs text-gray-500">Entry time</span>
              <input
                type="datetime-local"
                className="mt-1 w-full px-3 py-2 border rounded-lg dark:bg-gray-900 dark:border-gray-600"
                value={form.createdAt}
                onChange={(e) => setField('createdAt', e.target.value)}
              />
            </label>
            <label className="flex items-center gap-2 text-sm pt-6">
              <input
                type="checkbox"
                checked={form.dryRun}
                onChange={(e) => setField('dryRun', e.target.checked)}
              />
              Dry run
            </label>
          </div>
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save entry'}
            </button>
            <button
              type="button"
              onClick={cancelEdit}
              className="px-4 py-2 bg-gray-200 dark:bg-gray-700 rounded-lg"
            >
              Cancel
            </button>
          </div>
        </form>
      ) : null}

      {/* PnL hero */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-5 md:col-span-1 border border-gray-100 dark:border-gray-700">
          <div className="text-sm text-gray-500 mb-1">
            {event.dryRun === false || event.status === 'executed'
              ? 'Total PnL (incl. TP sells)'
              : 'Simulated PnL'}
          </div>
          <div className={`text-3xl font-bold ${pnlClass(pnl.pnlPct)}`}>{fmtPct(pnl.pnlPct)}</div>
          <div className={`text-sm mt-1 ${pnlClass(pnl.pnlUsd)}`}>{fmtUsd(pnl.pnlUsd)}</div>
          <div className="mt-3 space-y-1 text-xs text-gray-500">
            <div className="flex justify-between">
              <span>Cost</span>
              <span className="text-gray-800 dark:text-gray-200">{fmtUsd(pnl.entryUsd)}</span>
            </div>
            <div className="flex justify-between">
              <span>Realized TP</span>
              <span className={pnlClass(pnl.realizedUsd)}>{fmtUsd(pnl.realizedUsd)}</span>
            </div>
            <div className="flex justify-between">
              <span>Remaining MTM</span>
              <span className="text-gray-800 dark:text-gray-200">{fmtUsd(pnl.liveUsd)}</span>
            </div>
          </div>
          {pnl.mcMultiple != null && (
            <div className="text-xs text-gray-500 mt-3">
              MC multiple <span className="font-semibold text-gray-800 dark:text-gray-200">{Number(pnl.mcMultiple).toFixed(2)}x</span>
            </div>
          )}
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-5 border border-gray-100 dark:border-gray-700">
          <div className="text-sm text-gray-500 mb-2">At frontrun</div>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500">Market cap</span>
              <span className="font-medium text-gray-900 dark:text-white">{fmtUsd(pnl.entryMarketCap ?? event.marketCap)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Token price</span>
              <span className="font-medium text-gray-900 dark:text-white">{fmtUsd(pnl.entryPriceUsd ?? event.priceUsd, 6)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Position cost</span>
              <span className="font-medium text-gray-900 dark:text-white">{fmtUsd(pnl.entryUsd ?? event.estimatedUsd)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Tokens left</span>
              <span className="font-medium text-gray-900 dark:text-white">
                {pnl.entryTokens != null
                  ? Number(pnl.entryTokens).toLocaleString(undefined, { maximumFractionDigits: 4 })
                  : '—'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Native size</span>
              <span className="font-medium text-gray-900 dark:text-white">{event.amountNative}</span>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-5 border border-gray-100 dark:border-gray-700">
          <div className="text-sm text-gray-500 mb-2">Live now</div>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500">Market cap</span>
              <span className="font-medium text-gray-900 dark:text-white">{fmtUsd(pnl.liveMarketCap ?? live.marketCap)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Token price</span>
              <span className="font-medium text-gray-900 dark:text-white">{fmtUsd(pnl.livePriceUsd ?? live.priceUsd, 6)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Remaining value</span>
              <span className="font-medium text-gray-900 dark:text-white">{fmtUsd(pnl.liveUsd ?? live.valueUsd)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Liquidity</span>
              <span className="font-medium text-gray-900 dark:text-white">{fmtUsd(live.liquidityUsd)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Updated</span>
              <span className="text-xs text-gray-500">
                {live.fetchedAt ? new Date(live.fetchedAt).toLocaleTimeString() : '—'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {Array.isArray(pnl.sells) && pnl.sells.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-5 border border-gray-100 dark:border-gray-700">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Take-profit / sells</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="text-left text-xs text-gray-500">
                <tr>
                  <th className="py-1 pr-3">Type</th>
                  <th className="py-1 pr-3">%</th>
                  <th className="py-1 pr-3">Received</th>
                  <th className="py-1 pr-3">When</th>
                  <th className="py-1">Tx</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {pnl.sells.map((s, i) => (
                  <tr key={s.txHash || i}>
                    <td className="py-2 pr-3 font-medium text-gray-900 dark:text-white">
                      {String(s.reason || 'manual') === 'manual'
                        ? 'Manual TP'
                        : String(s.reason || '').startsWith('auto_')
                          ? `Auto ${String(s.reason).replace(/^auto_/, '').toUpperCase()}`
                          : s.reason}
                    </td>
                    <td className="py-2 pr-3">{s.percent}%</td>
                    <td className="py-2 pr-3">
                      <div>{fmtUsd(s.usdReceived)}</div>
                      <div className="text-xs text-gray-500">
                        {s.nativeReceived != null ? `${Number(s.nativeReceived).toFixed(4)} native` : '—'}
                      </div>
                    </td>
                    <td className="py-2 pr-3 text-xs text-gray-500">
                      {s.at ? new Date(s.at).toLocaleString() : '—'}
                    </td>
                    <td className="py-2 text-xs font-mono">
                      {s.txHash && event.links?.explorerTx
                        ? (
                          <a
                            href={String(event.links.explorerTx).replace(/\/tx\/.*/, `/tx/${s.txHash}`)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:underline"
                          >
                            {fmtAddr(s.txHash)}
                          </a>
                          )
                        : fmtAddr(s.txHash)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Meta */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-5 border border-gray-100 dark:border-gray-700">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Entry details</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
          <div>
            <div className="text-xs text-gray-500">Monitored wallet</div>
            <div className="font-medium text-gray-900 dark:text-white">
              {event.monitoredWalletLabel || '—'}
            </div>
            <div className="text-xs font-mono text-gray-500">{event.monitoredWalletAddress}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Frontrun time</div>
            <div className="font-medium text-gray-900 dark:text-white">
              {event.createdAt ? new Date(event.createdAt).toLocaleString() : '—'}
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Source tx</div>
            <div className="font-mono text-xs text-gray-700 dark:text-gray-300 break-all">
              {event.links?.explorerTx ? (
                <a href={event.links.explorerTx} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                  {event.sourceTx}
                </a>
              ) : (
                event.sourceTx || '—'
              )}
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Token</div>
            <div className="font-medium text-gray-900 dark:text-white">
              {event.tokenName || event.tokenSymbol || '—'}
            </div>
            <div className="text-xs font-mono text-gray-500 break-all">{event.tokenAddress}</div>
          </div>
          {target && (
            <div>
              <div className="text-xs text-gray-500">Target</div>
              <div className="font-medium text-gray-900 dark:text-white">{target.label || fmtAddr(target.walletAddress)}</div>
              <div className="text-xs text-gray-500">
                {(target.networks || []).join(', ') || target.network || '—'}
              </div>
            </div>
          )}
          {event.skipReason && (
            <div>
              <div className="text-xs text-gray-500">Skip reason</div>
              <div className="text-gray-800 dark:text-gray-200">{event.skipReason}</div>
            </div>
          )}
          {event.error && (
            <div>
              <div className="text-xs text-gray-500">Error</div>
              <div className="text-red-600">{event.error}</div>
            </div>
          )}
        </div>
      </div>

      <DexScreenerModal
        isOpen={chartOpen}
        onClose={() => setChartOpen(false)}
        chain={event.chain}
        tokenAddress={event.tokenAddress}
        tokenSymbol={event.tokenSymbol}
      />
    </div>
  );
}
